import React, { useState, useEffect } from 'react';
import Icon from '../Icon/Icon';
import Toggle from '../Toggle/Toggle';

const createEmptySizeEntry = () => ({ size_label: '', stock_quantity: '' });

const EditSizes = ({ 
    creation, 
    sizeEntries: initialSizeEntries, 
    isSizingAndQuantitiesEnabled: initialEnabled,
    onSave,
    onClose,
    onAddSize,
    onRemoveSize,
    onSizeChange,
    onToggleSizing
}) => {
    const [sizeEntries, setSizeEntries] = useState(initialSizeEntries || []);
    const [isSizingAndQuantitiesEnabled, setIsSizingAndQuantitiesEnabled] = useState(initialEnabled || false);
    const [sizeErrors, setSizeErrors] = useState([]);
    
    const edition_type = creation?.edition_type || 'unlimited';
    const isLimited = edition_type === 'limited';

    const handleAddSizeEntry = () => {
        setSizeEntries(prev => [...prev, createEmptySizeEntry()]);
        setSizeErrors([]);
    };

    const handleRemoveSizeEntry = (index) => {
        setSizeEntries(prev => prev.filter((_, i) => i !== index));
        setSizeErrors([]);
    };

    const handleSizeEntryChange = (index, field, value) => {
        setSizeEntries(prev => prev.map((entry, i) => i === index ? {
            ...entry,
            [field]: field === 'stock_quantity' ? (value === '' ? '' : value.replace(/[^0-9]/g, '')) : value
        } : entry));
        setSizeErrors([]);
    };

    const handleToggleSizing = (enabled) => {
        setIsSizingAndQuantitiesEnabled(enabled);
        setSizeErrors([]);
        if (enabled && sizeEntries.length === 0) {
            setSizeEntries([createEmptySizeEntry()]);
        }
        if (!enabled) {
            setSizeEntries([createEmptySizeEntry()]);
        }
    };

    const validateSizes = () => {
        const errors = [];
        
        if (isSizingAndQuantitiesEnabled) {
            if (sizeEntries.length === 0) {
                errors.push('At least one size entry is required');
            }
            
            const sizeLabels = sizeEntries.map(e => e.size_label?.trim()).filter(Boolean);
            const duplicates = sizeLabels.filter((label, index) => sizeLabels.indexOf(label) !== index);
            
            if (duplicates.length > 0) {
                errors.push('Duplicate size labels are not allowed');
            }
            
            sizeEntries.forEach((entry, index) => {
                if (!entry.size_label || entry.size_label.trim() === '') {
                    errors.push(`Size label is required for entry ${index + 1}`);
                }
                
                if (isLimited && entry.stock_quantity && parseInt(entry.stock_quantity, 10) < 0) {
                    errors.push(`Stock quantity must be positive for entry ${index + 1}`);
                }
            });
        }
        
        return errors;
    };

    const handleSave = () => {
        const errors = validateSizes();
        if (errors.length > 0) {
            setSizeErrors(errors);
            return;
        }
        
        const validEntries = isSizingAndQuantitiesEnabled
            ? sizeEntries.filter(entry => entry.size_label && entry.size_label.trim() !== '')
            : [];
        
        onSave(validEntries, isSizingAndQuantitiesEnabled);
    };

    return (
        <div className="add-creation__content modify-subscreen pt-40">
            <div className="d-flex aic jcc modify-header mb-8">
                <Icon alias={"chevron-left"} width={24} color="#0A0F1E" className={"p-absolute"} style={{ left: 16 }} onClick={onClose} />
                <span className="fg-black fw-bold fs-sm2">SIZING & QUANTITIES</span>
                <button
                    className="btn btn-sm red-btn rounded-xl fs-xxs p-fixed px-16"
                    style={{ right: '16px' }}
                    onClick={handleSave}
                >
                    Save
                </button>
            </div>
            
            <div className="mt-40 mx-16">
                <div className='d-flex f-row aic jcsb mb-16'>
                    <p className='fs-regular fw-medium fg-dark'>Sizing and Quantities</p>
                    <Toggle 
                        enabled={isSizingAndQuantitiesEnabled} 
                        setEnabled={handleToggleSizing} 
                    />
                </div>
                
                {isSizingAndQuantitiesEnabled && (
                    <div className="add-creation__copies">
                        {sizeEntries.map((entry, index) => (
                            <div
                                key={index}
                                className='d-flex f-row aic'
                                style={{ gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}
                            >
                                <input
                                    type="text"
                                    name={`size_label_${index}`}
                                    placeholder="Size label (S, M, L...)"
                                    value={entry.size_label}
                                    onChange={(e) => handleSizeEntryChange(index, 'size_label', e.target.value)}
                                    className="add-creation__input"
                                    style={{ flex: '1 1 180px' }}
                                />
                                {isLimited && (
                                    <input
                                        type="number"
                                        name={`stock_quantity_${index}`}
                                        placeholder="Stock (optional)"
                                        value={entry.stock_quantity}
                                        onChange={(e) => handleSizeEntryChange(index, 'stock_quantity', e.target.value)}
                                        className="add-creation__input"
                                        min="0"
                                        step="1"
                                        style={{ flex: '0 0 160px' }}
                                    />
                                )}
                                {sizeEntries.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSizeEntry(index)}
                                        className='fs-xxs fw-bold fg-red'
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        <div className='d-flex f-row aic'>
                            <button
                                type="button"
                                onClick={handleAddSizeEntry}
                                className='fg-red fs-xxs fw-extra-bold tt-uppercase td-underline td-solid'
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', alignSelf: 'flex-start' }}
                            >
                                Add Size
                            </button>
                            <Icon alias="plus" width={12} color="#FF3232" style={{marginLeft: '-8px'}}/>
                        </div>

                        {sizeErrors.length > 0 && (
                            <ul className='mt-8' style={{ paddingLeft: '16px', color: '#FF3232', fontSize: '12px', fontWeight: 600 }}>
                                {sizeErrors.map((msg, idx) => (
                                    <li key={idx}>{msg}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditSizes;


